import { useEffect, useMemo, useRef, useState, type BaseSyntheticEvent, type ReactNode } from 'react'
import {
  Controller,
  useWatch,
  type FieldErrors,
  type UseFormReturn,
} from 'react-hook-form'
import { CalendarClock, Check, CircleAlert, MapPin, Search, User, Users, Wallet } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CurrencyInput } from '@/components/common/currency-input'
import { SearchableSelect, type SearchableSelectOption } from '@/components/common/searchable-select'
import { OrderSelector } from '@/components/orders/order-selector'
import { JobLocationSearchField } from '@/components/jobs/job-location-search-field'
import {
  computeJobEndFromProcessing,
  formatJobScheduleDuration,
  combineJobDateTime,
  type JobProcessingUnit,
} from '@/lib/job-schedule-form'
import { getUserDisplayName } from '@/lib/user-display'
import { cn, formatCurrency, getCurrencySymbol, getInitials } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import type { StoreOrderSummary, Worker } from '@/types'

export type JobCreateRecurrenceRule =
  | 'none'
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'every_n_days'
  | 'weekly_days'
  | 'monthly_day'

/** Subset of the job create form values rendered by {@link JobCreateForm}. */
export type JobCreateFormFields = {
  title?: string
  order_reference_id?: string
  category: string
  client_name?: string
  phone?: string
  email?: string
  location?: string
  city?: string
  address?: string
  start_date: string
  start_time: string
  end_date?: string
  end_time?: string
  processing_time?: string
  processing_unit: JobProcessingUnit
  recurrence_rule: JobCreateRecurrenceRule
  recurrence_interval_days?: string
  recurrence_weekdays: number[]
  recurrence_month_day?: string
  recurrence_end?: string
  /** Sent as `job_pay`. */
  budget?: number | string
  advance_payment?: number | string
  staffed_team?: string
}

export type JobCreateAssignmentKind = 'worker' | 'team'

const RECURRENCE_OPTIONS: Array<{ value: JobCreateRecurrenceRule; label: string }> = [
  { value: 'none', label: 'Does not recur' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'every_n_days', label: 'After specific number of days' },
  { value: 'weekly_days', label: 'Weekly on selected days' },
  { value: 'monthly_day', label: 'Monthly on a specific day' },
]

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Field name → element id, in on-screen order, for focusing the first invalid field. */
const ERROR_FOCUS_ORDER: Array<[keyof JobCreateFormFields, string]> = [
  ['title', 'title'],
  ['order_reference_id', 'order-search'],
  ['category', 'category'],
  ['client_name', 'client_name'],
  ['phone', 'phone'],
  ['email', 'email'],
  ['location', 'location'],
  ['address', 'address'],
  ['start_date', 'start_date'],
  ['start_time', 'start_time'],
  ['processing_time', 'processing_time'],
  ['recurrence_rule', 'recurrence_rule'],
  ['recurrence_interval_days', 'recurrence_interval_days'],
  ['recurrence_weekdays', 'recurrence_weekdays'],
  ['recurrence_month_day', 'recurrence_month_day'],
  ['recurrence_end', 'recurrence_end'],
  ['budget', 'budget'],
  ['advance_payment', 'advance_payment'],
]

export const ADVANCE_EXCEEDS_BUDGET_MESSAGE = 'Advance cannot be more than the budget amount'

/** Parses an amount input value; blank or invalid input is `null`. */
const parseAmount = (value: unknown) => {
  const text = String(value ?? '').trim()
  if (!text) {
    return null
  }
  const amount = Number(text)
  return Number.isFinite(amount) ? amount : null
}

/** Scrolls to and focuses the first invalid field of the job create form. */
export function focusFirstJobCreateFormError(errors: FieldErrors) {
  for (const [name, elementId] of ERROR_FOCUS_ORDER) {
    if (!errors[name]) {
      continue
    }
    const element = document.getElementById(elementId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      element.focus({ preventScroll: true })
    }
    return
  }
}

export function bindJobCreateForm<T extends JobCreateFormFields>(form: UseFormReturn<T>) {
  return form as unknown as UseFormReturn<JobCreateFormFields>
}

type FieldProps = {
  id: string
  label: string
  required?: boolean
  helper?: ReactNode
  error?: string
  className?: string
  children: ReactNode
}

function Field({ id, label, required = false, helper, error, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? <span className="text-destructive" aria-hidden> *</span> : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </Label>
      {children}
      {helper && !error ? <p id={`${id}-help`} className="text-xs text-muted-foreground">{helper}</p> : null}
      {error ? (
        <p id={`${id}-error`} className="flex items-center gap-1 text-sm text-destructive">
          <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  )
}

const describedBy = (id: string, error?: string, hasHelper = false) =>
  error ? `${id}-error` : hasHelper ? `${id}-help` : undefined

type FormSectionProps = {
  id: string
  step: number
  title: string
  description?: string
  icon: ReactNode
  action?: ReactNode
  children: ReactNode
}

function FormSection({ id, step, title, description, icon, action, children }: FormSectionProps) {
  return (
    <Card id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex items-start gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
            aria-hidden
          >
            {icon}
          </span>
          <div className="space-y-1">
            <CardTitle id={`${id}-title`} className="text-base">
              <span className="sr-only">{`Step ${step}: `}</span>
              {title}
            </CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </div>
        </div>
        {action}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  )
}

type JobCreateFormProps = {
  form: UseFormReturn<JobCreateFormFields>
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>
  onCancel: () => void
  /** Show the Recurrence and Repeat until fields (Add Recurrence Job page). */
  showRecurrence?: boolean
  serviceInstanceId?: string | number | null
  submitLabel: string
  isSubmitting: boolean
  submitDisabled: boolean
  categoryOptions: SearchableSelectOption[]
  isCategoriesLoading: boolean
  selectedOrder: StoreOrderSummary | null
  onOrderSelect: (order: StoreOrderSummary | null) => void
  workers: Worker[]
  isWorkersLoading: boolean
  selectedWorkerIds: string[]
  onSelectedWorkerIdsChange: (ids: string[]) => void
  assignmentKind: JobCreateAssignmentKind
  onAssignmentKindChange: (kind: JobCreateAssignmentKind) => void
  teamOptions: SearchableSelectOption[]
  isTeamsLoading: boolean
}

export function JobCreateForm({
  form,
  onSubmit,
  onCancel,
  showRecurrence = false,
  serviceInstanceId,
  submitLabel,
  isSubmitting,
  submitDisabled,
  categoryOptions,
  isCategoriesLoading,
  selectedOrder,
  onOrderSelect,
  workers,
  isWorkersLoading,
  selectedWorkerIds,
  onSelectedWorkerIdsChange,
  assignmentKind,
  onAssignmentKindChange,
  teamOptions,
  isTeamsLoading,
}: JobCreateFormProps) {
  const { control, register, setValue, getValues, formState: { errors } } = form
  const values = useWatch({ control })
  const recurrenceRule = values.recurrence_rule ?? 'none'
  const noCategories = !isCategoriesLoading && categoryOptions.length === 0
  const budgetAmount = parseAmount(values.budget)
  const advanceAmount = parseAmount(values.advance_payment)
  // Same currency formatCurrency uses: the active service instance's, then the branch's.
  const activeCurrency = useAuthStore((state) => state.activeCurrency)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currencySymbol = useMemo(() => getCurrencySymbol(), [activeCurrency])
  // Flag the advance as soon as both amounts are typed; submit is blocked by the page schema.
  const advanceError = errors.advance_payment?.message
    ?? (budgetAmount != null && advanceAmount != null && advanceAmount > budgetAmount
      ? ADVANCE_EXCEEDS_BUDGET_MESSAGE
      : undefined)

  // End date/time are read-only here and always follow start + processing time.
  // Only clear values this form calculated, so an AI-drafted end is kept.
  const lastComputedEndRef = useRef<{ date: string; time: string } | null>(null)
  useEffect(() => {
    const computed = computeJobEndFromProcessing(
      values.start_date,
      values.start_time,
      values.processing_time,
      values.processing_unit ?? 'hr',
    )
    const currentDate = values.end_date ?? ''
    const currentTime = values.end_time ?? ''

    if (computed) {
      lastComputedEndRef.current = computed
      if (computed.date !== currentDate || computed.time !== currentTime) {
        setValue('end_date', computed.date, { shouldDirty: true })
        setValue('end_time', computed.time, { shouldDirty: true })
      }
      return
    }

    const last = lastComputedEndRef.current
    if (last && last.date === currentDate && last.time === currentTime) {
      lastComputedEndRef.current = null
      setValue('end_date', '', { shouldDirty: true })
      setValue('end_time', '', { shouldDirty: true })
    }
  }, [
    setValue,
    values.end_date,
    values.end_time,
    values.processing_time,
    values.processing_unit,
    values.start_date,
    values.start_time,
  ])

  return (
    <form onSubmit={onSubmit} noValidate aria-label={submitLabel}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="min-w-0 space-y-6">
          {/* 1. Basic information */}
          <FormSection
            id="job-section-basic"
            step={1}
            title="Basic information"
            description="What the job is and how it is categorised."
            icon={<Check className="h-4 w-4" />}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field
                id="title"
                label="Job title"
                helper="Leave blank to use the category name."
                error={errors.title?.message}
              >
                <Input
                  id="title"
                  placeholder="e.g. Deep clean – 2 bed flat"
                  aria-invalid={!!errors.title}
                  aria-describedby={describedBy('title', errors.title?.message, true)}
                  {...register('title')}
                />
              </Field>

              <div className="space-y-2">
                <OrderSelector
                  label="Booking ID (optional)"
                  value={values.order_reference_id}
                  selectedOrder={selectedOrder}
                  onSelect={onOrderSelect}
                  error={errors.order_reference_id?.message}
                />
                <p className="text-xs text-muted-foreground">Link this job to an existing booking.</p>
              </div>

              <Field
                id="category"
                label="Category"
                required
                helper={noCategories ? 'Create a category first before creating a job.' : 'Workers are filtered by this category.'}
                error={errors.category?.message}
              >
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <SearchableSelect
                      id="category"
                      ariaLabel="Category"
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      options={categoryOptions}
                      placeholder={isCategoriesLoading ? 'Loading categories...' : 'Search or select category'}
                      searchPlaceholder="Type a category name"
                      emptyText="No matching categories found."
                      emptySearchText="Start typing a category name."
                      disabled={isCategoriesLoading || categoryOptions.length === 0}
                    />
                  )}
                />
              </Field>

              <Field id="job_status" label="Status" helper="New jobs are saved as Draft.">
                <Select value="draft" disabled>
                  <SelectTrigger id="job_status" aria-describedby="job_status-help">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </FormSection>

          {/* 2. Customer */}
          <FormSection
            id="job-section-customer"
            step={2}
            title="Customer"
            description="Who the job is for."
            icon={<User className="h-4 w-4" />}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field id="client_name" label="Customer name" error={errors.client_name?.message} className="md:col-span-2">
                <Input
                  id="client_name"
                  autoComplete="name"
                  placeholder="Full name"
                  aria-invalid={!!errors.client_name}
                  aria-describedby={describedBy('client_name', errors.client_name?.message)}
                  {...register('client_name')}
                />
              </Field>
              <Field id="phone" label="Phone number" error={errors.phone?.message}>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. 07700 900123"
                  aria-invalid={!!errors.phone}
                  aria-describedby={describedBy('phone', errors.phone?.message)}
                  {...register('phone')}
                />
              </Field>
              <Field id="email" label="Email (optional)" error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={describedBy('email', errors.email?.message)}
                  {...register('email')}
                />
              </Field>
            </div>
          </FormSection>

          {/* 3. Location */}
          <FormSection
            id="job-section-location"
            step={3}
            title="Location"
            description="Where the work takes place."
            icon={<MapPin className="h-4 w-4" />}
          >
            <Field
              id="location"
              label="Search location"
              helper="Pick a result to fill the address below."
              error={errors.location?.message}
            >
              <Controller
                control={control}
                name="location"
                render={({ field }) => (
                  <JobLocationSearchField
                    id="location"
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    serviceInstanceId={serviceInstanceId}
                    invalid={!!errors.location}
                    describedBy={describedBy('location', errors.location?.message, true)}
                    onPlaceSelected={(place) => {
                      setValue('city', place.city, { shouldDirty: true })
                      if (!String(getValues('address') ?? '').trim()) {
                        setValue('address', place.label, { shouldDirty: true, shouldValidate: true })
                      }
                    }}
                  />
                )}
              />
            </Field>
            <Field
              id="address"
              label="Address"
              helper="Filled from the selected location when empty. You can edit it."
              error={errors.address?.message}
            >
              <Textarea
                id="address"
                rows={2}
                placeholder="House number, street, postcode"
                autoComplete="street-address"
                aria-invalid={!!errors.address}
                aria-describedby={describedBy('address', errors.address?.message, true)}
                {...register('address')}
              />
            </Field>
          </FormSection>

          {/* 4. Schedule */}
          <FormSection
            id="job-section-schedule"
            step={4}
            title="Schedule"
            description={showRecurrence ? 'When the first job starts and how often it repeats.' : 'When the job starts and how long it takes.'}
            icon={<CalendarClock className="h-4 w-4" />}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="start_date" label="Start date" required error={errors.start_date?.message}>
                <Input
                  id="start_date"
                  type="date"
                  aria-required
                  aria-invalid={!!errors.start_date}
                  aria-describedby={describedBy('start_date', errors.start_date?.message)}
                  {...register('start_date')}
                />
              </Field>
              <Field id="start_time" label="Start time" required error={errors.start_time?.message}>
                <Input
                  id="start_time"
                  type="time"
                  step={60}
                  aria-required
                  aria-invalid={!!errors.start_time}
                  aria-describedby={describedBy('start_time', errors.start_time?.message)}
                  {...register('start_time')}
                />
              </Field>
            </div>

            <Field
              id="processing_time"
              label="Processing time"
              helper="End date and time are calculated from start + processing time."
              error={errors.processing_time?.message}
            >
              <div className="grid grid-cols-[minmax(0,1fr)_130px] gap-2">
                <Input
                  id="processing_time"
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  placeholder="e.g. 2"
                  aria-invalid={!!errors.processing_time}
                  aria-describedby={describedBy('processing_time', errors.processing_time?.message, true)}
                  {...register('processing_time')}
                />
                <Controller
                  control={control}
                  name="processing_unit"
                  render={({ field }) => (
                    <Select value={field.value ?? 'hr'} onValueChange={field.onChange}>
                      <SelectTrigger id="processing_unit" aria-label="Processing time unit">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="min">Minutes</SelectItem>
                        <SelectItem value="hr">Hours</SelectItem>
                        <SelectItem value="day">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="end_date" label="End date" helper="Calculated">
                <Input
                  id="end_date"
                  type="date"
                  readOnly
                  tabIndex={-1}
                  value={values.end_date ?? ''}
                  aria-readonly
                  aria-describedby="end_date-help"
                  className="bg-muted/50 text-muted-foreground"
                />
              </Field>
              <Field id="end_time" label="End time" helper="Calculated">
                <Input
                  id="end_time"
                  type="time"
                  readOnly
                  tabIndex={-1}
                  value={values.end_time ?? ''}
                  aria-readonly
                  aria-describedby="end_time-help"
                  className="bg-muted/50 text-muted-foreground"
                />
              </Field>
            </div>

            {showRecurrence ? (
              <div className="space-y-4 border-t border-border/60 pt-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field id="recurrence_rule" label="Recurrence" error={errors.recurrence_rule?.message}>
                    <Controller
                      control={control}
                      name="recurrence_rule"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="recurrence_rule">
                            <SelectValue placeholder="Select recurrence" />
                          </SelectTrigger>
                          <SelectContent>
                            {RECURRENCE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>

                  {recurrenceRule !== 'none' ? (
                    <Field
                      id="recurrence_end"
                      label="Repeat until"
                      helper="Leave blank to repeat with no end date."
                      error={errors.recurrence_end?.message}
                    >
                      <Input
                        id="recurrence_end"
                        type="date"
                        min={values.start_date || undefined}
                        aria-describedby={describedBy('recurrence_end', errors.recurrence_end?.message, true)}
                        {...register('recurrence_end')}
                      />
                    </Field>
                  ) : null}
                </div>

                {recurrenceRule === 'every_n_days' ? (
                  <Field id="recurrence_interval_days" label="Repeat every N days" error={errors.recurrence_interval_days?.message}>
                    <Input
                      id="recurrence_interval_days"
                      type="number"
                      min="1"
                      inputMode="numeric"
                      placeholder="Enter days"
                      className="sm:max-w-[200px]"
                      aria-invalid={!!errors.recurrence_interval_days}
                      aria-describedby={describedBy('recurrence_interval_days', errors.recurrence_interval_days?.message)}
                      {...register('recurrence_interval_days')}
                    />
                  </Field>
                ) : null}

                {recurrenceRule === 'weekly_days' ? (
                  <div className="space-y-2">
                    <p id="recurrence_weekdays-label" className="text-sm font-medium leading-none">Repeat on these days</p>
                    <Controller
                      control={control}
                      name="recurrence_weekdays"
                      render={({ field }) => (
                        <div
                          id="recurrence_weekdays"
                          role="group"
                          tabIndex={-1}
                          aria-labelledby="recurrence_weekdays-label"
                          aria-describedby={errors.recurrence_weekdays ? 'recurrence_weekdays-error' : undefined}
                          className="flex flex-wrap gap-2 rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        >
                          {WEEKDAY_LABELS.map((label, dayIndex) => {
                            const weekdays = field.value ?? []
                            const isSelected = weekdays.includes(dayIndex)
                            return (
                              <Button
                                key={label}
                                type="button"
                                size="sm"
                                variant={isSelected ? 'default' : 'outline'}
                                aria-pressed={isSelected}
                                className="min-w-12"
                                onClick={() =>
                                  field.onChange(
                                    isSelected
                                      ? weekdays.filter((day) => day !== dayIndex)
                                      : [...weekdays, dayIndex].sort((a, b) => a - b),
                                  )
                                }
                              >
                                {label}
                              </Button>
                            )
                          })}
                        </div>
                      )}
                    />
                    {errors.recurrence_weekdays ? (
                      <p id="recurrence_weekdays-error" className="flex items-center gap-1 text-sm text-destructive">
                        <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {errors.recurrence_weekdays.message}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {recurrenceRule === 'monthly_day' ? (
                  <Field id="recurrence_month_day" label="Day of month" error={errors.recurrence_month_day?.message}>
                    <Input
                      id="recurrence_month_day"
                      type="number"
                      min="1"
                      max="31"
                      inputMode="numeric"
                      placeholder="e.g. 15"
                      className="sm:max-w-[200px]"
                      aria-invalid={!!errors.recurrence_month_day}
                      aria-describedby={describedBy('recurrence_month_day', errors.recurrence_month_day?.message)}
                      {...register('recurrence_month_day')}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}
          </FormSection>

          {/* 5. Payment */}
          <FormSection
            id="job-section-payment"
            step={5}
            title="Payment"
            description="The job budget and any amount paid upfront."
            icon={<Wallet className="h-4 w-4" />}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="budget" label="Budget amount (optional)" error={errors.budget?.message}>
                <CurrencyInput
                  id="budget"
                  currencySymbol={currencySymbol}
                  inputMode="decimal"
                  placeholder="0.00"
                  aria-invalid={!!errors.budget}
                  aria-describedby={describedBy('budget', errors.budget?.message)}
                  {...register('budget')}
                />
              </Field>
              <Field
                id="advance_payment"
                label="Advance payment (optional)"
                helper={budgetAmount != null
                  ? `Balance after advance: ${formatCurrency(budgetAmount - (advanceAmount ?? 0))}`
                  : 'Amount already paid upfront.'}
                error={advanceError}
              >
                <CurrencyInput
                  id="advance_payment"
                  currencySymbol={currencySymbol}
                  inputMode="decimal"
                  placeholder="0.00"
                  aria-invalid={!!advanceError}
                  aria-describedby={describedBy('advance_payment', advanceError, true)}
                  {...register('advance_payment')}
                />
              </Field>
            </div>
          </FormSection>

          {/* 6. Add workers */}
          <WorkersSection
            hasCategory={Boolean(values.category)}
            workers={workers}
            isWorkersLoading={isWorkersLoading}
            selectedWorkerIds={selectedWorkerIds}
            onSelectedWorkerIdsChange={onSelectedWorkerIdsChange}
            assignmentKind={assignmentKind}
            onAssignmentKindChange={onAssignmentKindChange}
            teamField={(
              <Controller
                control={control}
                name="staffed_team"
                render={({ field }) => (
                  <SearchableSelect
                    id="add_team"
                    ariaLabel="Team"
                    value={field.value && field.value !== 'none' ? field.value : ''}
                    onValueChange={field.onChange}
                    options={teamOptions}
                    placeholder={isTeamsLoading ? 'Loading teams...' : 'Search teams'}
                    searchPlaceholder="Search teams"
                    emptyText="No teams found."
                    disabled={isTeamsLoading}
                  />
                )}
              />
            )}
          />
        </div>

        <aside className="lg:sticky lg:top-4" aria-label="Job summary">
          <JobSummaryPanel
            values={values}
            showRecurrence={showRecurrence}
            categoryOptions={categoryOptions}
            selectedOrder={selectedOrder}
            assignmentKind={assignmentKind}
            selectedWorkerCount={selectedWorkerIds.length}
            teamOptions={teamOptions}
          />
        </aside>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button type="submit" disabled={submitDisabled} className="w-full sm:w-auto">
            {isSubmitting ? <Spinner className="mr-2" /> : null}
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

type WorkersSectionProps = {
  hasCategory: boolean
  workers: Worker[]
  isWorkersLoading: boolean
  selectedWorkerIds: string[]
  onSelectedWorkerIdsChange: (ids: string[]) => void
  assignmentKind: JobCreateAssignmentKind
  onAssignmentKindChange: (kind: JobCreateAssignmentKind) => void
  teamField: ReactNode
}

const segmentClassName = (active: boolean) =>
  cn(
    'rounded-md px-3 py-1 text-xs font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active
      ? 'bg-background text-foreground shadow-sm'
      : 'text-muted-foreground hover:text-foreground',
  )

function WorkersSection({
  hasCategory,
  workers,
  isWorkersLoading,
  selectedWorkerIds,
  onSelectedWorkerIdsChange,
  assignmentKind,
  onAssignmentKindChange,
  teamField,
}: WorkersSectionProps) {
  const [search, setSearch] = useState('')
  const selectedSet = useMemo(() => new Set(selectedWorkerIds), [selectedWorkerIds])

  const workerRows = useMemo(
    () => workers.map((worker) => {
      const name = getUserDisplayName(worker, worker.id)
      const record = worker as Worker & { phone?: string | null; user_registration_number?: string | null }
      const secondary = [record.user_registration_number, record.phone]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .join(' · ')
      return { id: String(worker.id), name, secondary }
    }),
    [workers],
  )

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) {
      return workerRows
    }
    return workerRows.filter((row) => `${row.name} ${row.secondary}`.toLowerCase().includes(term))
  }, [search, workerRows])

  const filteredSelectedCount = filteredRows.filter((row) => selectedSet.has(row.id)).length
  const allFilteredSelected = filteredRows.length > 0 && filteredSelectedCount === filteredRows.length
  const selectAllState: boolean | 'indeterminate' = allFilteredSelected
    ? true
    : filteredSelectedCount > 0
      ? 'indeterminate'
      : false

  const toggleWorker = (workerId: string, checked: boolean) => {
    onSelectedWorkerIdsChange(
      checked
        ? [...selectedWorkerIds.filter((id) => id !== workerId), workerId]
        : selectedWorkerIds.filter((id) => id !== workerId),
    )
  }

  const toggleAllFiltered = (checked: boolean) => {
    const filteredIds = filteredRows.map((row) => row.id)
    if (checked) {
      onSelectedWorkerIdsChange([...new Set([...selectedWorkerIds, ...filteredIds])])
    } else {
      const filteredSet = new Set(filteredIds)
      onSelectedWorkerIdsChange(selectedWorkerIds.filter((id) => !filteredSet.has(id)))
    }
  }

  return (
    <FormSection
      id="job-section-workers"
      step={6}
      title="Add workers"
      description={assignmentKind === 'worker' ? 'Workers who match the selected category.' : 'Assign a ready staffed team.'}
      icon={<Users className="h-4 w-4" />}
      action={(
        <div className="inline-flex shrink-0 rounded-lg border border-border/70 bg-muted/40 p-0.5" role="group" aria-label="Assign by">
          <button
            type="button"
            aria-pressed={assignmentKind === 'worker'}
            className={segmentClassName(assignmentKind === 'worker')}
            onClick={() => onAssignmentKindChange('worker')}
          >
            Workers
          </button>
          <button
            type="button"
            aria-pressed={assignmentKind === 'team'}
            className={segmentClassName(assignmentKind === 'team')}
            onClick={() => onAssignmentKindChange('team')}
          >
            Team
          </button>
        </div>
      )}
    >
      {assignmentKind === 'team' ? (
        <div className="space-y-2">
          <Label htmlFor="add_team">Team</Label>
          {teamField}
        </div>
      ) : !hasCategory ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          Select a category to see matching workers.
        </div>
      ) : isWorkersLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground" role="status">
          <Spinner />
          Loading workers...
        </div>
      ) : workerRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No workers are mapped to the selected category.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search workers"
              aria-label="Search workers"
              className="pl-9"
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={selectAllState}
                onCheckedChange={(checked) => toggleAllFiltered(checked === true)}
                disabled={filteredRows.length === 0}
                aria-label={search.trim() ? 'Select all matching workers' : 'Select all workers'}
              />
              Select all
            </label>
            <Badge variant={selectedWorkerIds.length > 0 ? 'default' : 'secondary'} aria-live="polite">
              {`${selectedWorkerIds.length} selected`}
            </Badge>
          </div>

          {filteredRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No workers match your search.</p>
          ) : (
            <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border border-border" aria-label="Workers">
              {filteredRows.map((row) => {
                const checked = selectedSet.has(row.id)
                const checkboxId = `job-worker-${row.id}`
                return (
                  <li key={row.id}>
                    <label
                      htmlFor={checkboxId}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50',
                        checked && 'bg-primary/5',
                      )}
                    >
                      <Checkbox
                        id={checkboxId}
                        checked={checked}
                        onCheckedChange={(next) => toggleWorker(row.id, next === true)}
                      />
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                        aria-hidden
                      >
                        {getInitials(row.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">{row.name}</span>
                        {row.secondary ? (
                          <span className="block truncate text-xs text-muted-foreground">{row.secondary}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">Selected workers are assigned once the job is created.</p>
        </div>
      )}
    </FormSection>
  )
}

type JobSummaryPanelProps = {
  values: Partial<JobCreateFormFields>
  showRecurrence: boolean
  categoryOptions: SearchableSelectOption[]
  selectedOrder: StoreOrderSummary | null
  assignmentKind: JobCreateAssignmentKind
  selectedWorkerCount: number
  teamOptions: SearchableSelectOption[]
}

const formatSummaryDateTime = (date?: string, time?: string) => {
  const iso = combineJobDateTime(date, time)
  if (!iso) {
    return null
  }
  const parsed = new Date(iso)
  if (!Number.isFinite(parsed.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(time ? { hour: 'numeric', minute: '2-digit' } : {}),
  }).format(parsed)
}

function JobSummaryPanel({
  values,
  showRecurrence,
  categoryOptions,
  selectedOrder,
  assignmentKind,
  selectedWorkerCount,
  teamOptions,
}: JobSummaryPanelProps) {
  const categoryLabel = categoryOptions.find((option) => option.value === values.category)?.label
  const requiredChecks = [
    { label: 'Category', done: Boolean(values.category) },
    { label: 'Start date', done: Boolean(values.start_date?.trim()) },
    { label: 'Start time', done: Boolean(values.start_time?.trim()) },
  ]
  const doneCount = requiredChecks.filter((check) => check.done).length
  const progress = Math.round((doneCount / requiredChecks.length) * 100)

  const recurrenceLabel = RECURRENCE_OPTIONS.find((option) => option.value === (values.recurrence_rule ?? 'none'))?.label
  const duration = formatJobScheduleDuration(
    combineJobDateTime(values.start_date, values.start_time),
    combineJobDateTime(values.end_date, values.end_time),
  )
  const teamLabel = teamOptions.find((option) => option.value === values.staffed_team)?.label
  const budgetAmount = parseAmount(values.budget)
  const advanceAmount = parseAmount(values.advance_payment)

  const rows: Array<{ label: string; value: string | null | undefined }> = [
    { label: 'Title', value: values.title?.trim() || (categoryLabel ? `${categoryLabel} (from category)` : null) },
    { label: 'Category', value: categoryLabel },
    { label: 'Booking', value: selectedOrder?.reference_id || values.order_reference_id },
    { label: 'Customer', value: [values.client_name, values.phone].map((part) => part?.trim()).filter(Boolean).join(' · ') },
    { label: 'Location', value: values.address?.trim() || values.location?.trim() },
    { label: 'Starts', value: formatSummaryDateTime(values.start_date, values.start_time) },
    { label: 'Ends', value: formatSummaryDateTime(values.end_date, values.end_time) },
    { label: 'Duration', value: duration },
    ...(showRecurrence
      ? [{
          label: 'Repeats',
          value: values.recurrence_rule && values.recurrence_rule !== 'none'
            ? `${recurrenceLabel}${values.recurrence_end ? ` until ${formatSummaryDateTime(values.recurrence_end)}` : ''}`
            : recurrenceLabel,
        }]
      : []),
    {
      label: 'Budget · Advance',
      value: budgetAmount != null || advanceAmount != null
        ? [budgetAmount, advanceAmount].map((amount) => (amount != null ? formatCurrency(amount) : '—')).join(' · ')
        : null,
    },
    {
      label: assignmentKind === 'team' ? 'Team' : 'Workers',
      value: assignmentKind === 'team'
        ? teamLabel
        : selectedWorkerCount > 0 ? `${selectedWorkerCount} selected` : null,
    },
  ]

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Summary</CardTitle>
          <Badge variant="secondary">Draft</Badge>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span id="job-summary-progress-label">Required fields</span>
            <span>{`${doneCount} of ${requiredChecks.length}`}</span>
          </div>
          <Progress value={progress} aria-labelledby="job-summary-progress-label" aria-valuetext={`${doneCount} of ${requiredChecks.length} required fields complete`} />
          <ul className="flex flex-wrap gap-x-3 gap-y-1 pt-1 text-xs">
            {requiredChecks.map((check) => (
              <li
                key={check.label}
                className={cn('flex items-center gap-1', check.done ? 'text-foreground' : 'text-muted-foreground')}
              >
                {check.done
                  ? <Check className="h-3 w-3 text-primary" aria-hidden />
                  : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" aria-hidden />}
                {check.label}
                <span className="sr-only">{check.done ? ' complete' : ' missing'}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border text-sm">
          {rows.map((row) => (
            <div key={row.label} className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 py-2">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className={cn('break-words', row.value ? 'text-foreground' : 'text-muted-foreground/70')}>
                {row.value || '—'}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}
